import { NumberParam, StringParam } from "use-query-params";
import { DuringDayOnlySwitch } from "../DuringDayOnlySwitch";
import { ActivityChanceGraph } from "../ActivityChanceGraph";
import { ActivityLimitsTable } from "../ActivityLimitsTable";
import { SanitizedDynamicHtml } from "../SanitizedDynamicHtml";
import { SportSetting } from "../SportSetting";
import { useQueryCookieParam } from "../../util/useQueryCookieParam";
import React, { Dispatch, SetStateAction, useContext } from "react";
import { useQueryStateParam } from "../../util/useQueryStateParam";
import { Center } from "@bedrock-layout/center";
import { TideGraphs } from "../TideGraphs";
import { getSportFromHostname, SC_BASE_PATH } from "../../util/util";
import { useRemoteData } from "../../util/useRemoteData";
import { DateTime } from "luxon";
import { relDaysFromName } from "../../util/util";
import { getColFromSpotHarm } from "../chartParts/GetColFromSpotHarm";

import {
  ActivityData,
  ActivityLimits,
  ExtraGraphsData,
  GraphData,
  GraphsData,
  SpotHarmData,
  KiteGear,
  LocationData,
  SoarGear,
  SpotData,
  TideWindData,
  WindowSize,
} from "../../datastructures";
import { AppContext } from "../../util/Contexts";
import { ActueleWindMeting } from "../ActueleWindMeting";
import { NkvSpotLinks } from "../NkvSpotLinks";
import { ActueelGetij } from "../ActueelGetij";
import ConditionalRender from "../ConditionalRender";
import { MeasurementSourceLink } from "../MeasurementSourceLink";
import { SpotForecastGraph } from "../SpotForecastGraph";
import InfoOverlay from "../InfoOverlay";

const DEFAULT_LOCATION: LocationData = {
  id: 0,
  location_name: "Loading location data...",
  lat: 0,
  lon: 0,
  n: 0,
  e: 0,
  col_generation_enabled: false,
  harm: false,
  gfs: false,
  gefs: false,
  sc_app: false,
  w2k_app: false,
  sc_site: false,
  w2k_site: false,
  source: "",
};

interface SpotInfoProperties {
  spotData: SpotData[];
  locationData: LocationData[];
  windUnits: string;
  kiteGear: KiteGear;
  soarGear: SoarGear;
  activitiesData: ActivityData[];
  graphsData: GraphsData[];
  extraGraphsData: ExtraGraphsData[];
  actLimitData: ActivityLimits[];
  duringDayOnly: boolean;
  setDuringDayOnly: Dispatch<SetStateAction<boolean>>;
  windowSize: WindowSize;
  showSpotHarmonie: boolean;
  setShowSpotHarmonie: (newValue: boolean) => void;
  tideWindData: TideWindData[];
}

export const SpotInfo: React.FC<SpotInfoProperties> = ({
  spotData,
  locationData,
  windUnits,
  kiteGear,
  soarGear,
  activitiesData,
  graphsData,
  extraGraphsData,
  actLimitData,
  duringDayOnly,
  setDuringDayOnly,
  windowSize,
  showSpotHarmonie,
  setShowSpotHarmonie,
  tideWindData,
}) => {
  const appContextData = useContext(AppContext);

  const [day, setDay] = useQueryStateParam<string>(
    "day",
    StringParam,
    appContextData?.dayState,
    appContextData?.setDayState,
  );

  const sportFromHostname = getSportFromHostname();

  const [sport, setSport] = useQueryCookieParam(
    "sport",
    StringParam,
    sportFromHostname,
  );

  const [spot, setSpot] = useQueryCookieParam<number>("spot", NumberParam, 1);

  const spotDataForSport = spotData.filter(
    (s) => (sport === "kite" && s.kite) || (sport === "soar" && s.soar),
  );

  const selectedKiteActivity = activitiesData.find(
    (a) =>
      a.kiten &&
      a.spot_id === spot &&
      a.bigkite === kiteGear.bigKite &&
      a.medkite === kiteGear.medKite &&
      a.smallkite === kiteGear.smallKite,
  );

  const selectedSoarActivity = activitiesData.find(
    (a) =>
      a.soaren &&
      a.spot_id === spot &&
      a.bigwing === soarGear.bigWing &&
      a.smallwing === soarGear.smallWing,
  );

  const onSpotChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    setSpot(parseInt(e.target.value));
  };

  const selectedAct =
    sport === "soar" ? selectedSoarActivity : selectedKiteActivity;

  const activity = selectedAct?.id;

  const selectedActName = selectedAct?.name || "Onbekende activiteit";

  const selectedActSpotId = selectedAct?.spot_id;

  const selectedGraphs = graphsData
    ?.filter((a) => a.activity_id === activity)
    .sort((a, b) => b.unifiedunits.localeCompare(a.unifiedunits));

  const selectedExtraGraphs = extraGraphsData?.filter(
    (a) => a.act_id === activity,
  );

  const selectedSpotData = spotData.find((s) => s.id === selectedActSpotId);
  const selectedSpotName = selectedSpotData?.name || "Onbekende spot";

  const fakeSpotForSpotNa = { id: 0, name: "Selecteer spot" };
  const spotDropdownData = spotDataForSport
    ? selectedSpotData
      ? spotDataForSport
      : [fakeSpotForSpotNa, ...spotDataForSport]
    : [];

  const selectedSpotRemarks = selectedSpotData?.remarks;
  const selectedActRemarks = selectedAct?.remarks;

  // Filter the passed tideWindData for the selected spot
  const filteredTideWindData = tideWindData.filter((x) => x.spot_id === spot);

  const locationById = function (locationId?: number): LocationData {
    return locationData.find((l) => l.id === locationId) || DEFAULT_LOCATION;
  };

  const windLocationData: LocationData | undefined = locationById(
    selectedSpotData?.wind_location,
  );

  // raw spot harmonie fetch
  const spotHarmonieUrl =
    showSpotHarmonie && spot
      ? SC_BASE_PATH + "/scapi.php?table=latest_spot_harm_" + spot
      : null;
  const rawSpotHarmonie = useRemoteData<any[]>(spotHarmonieUrl, [
    spot,
    showSpotHarmonie,
  ]);

  function filterSpotHarmonieByDay(
    data: any[],
    day: string,
    duringDayOnly: boolean,
  ): any[] {
    const midnight = DateTime.local({ zone: "Europe/Amsterdam" })
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .plus({ days: relDaysFromName(day) });
    const start = midnight.plus({ hours: duringDayOnly ? 7 : 0 });
    const end = midnight
      .plus({ days: 1 })
      .minus({ hours: duringDayOnly ? 5 : 0 });
    return data.filter(
      (row) =>
        row.etime >= start.toUnixInteger() && row.etime <= end.toUnixInteger(),
    );
  }

  function transformSpotHarmonie(data: any[]): SpotHarmData | null {
    if (!data || data.length === 0) {
      return null;
    }
    const datasetsDeg: GraphData[] = [
      addNorthWindExtraPoints(
        getColFromSpotHarm(data, "windrichting", "Spot richting voorsp."),
      ),
    ];
    const datasetsMS: GraphData[] = [
      getColFromSpotHarm(data, "windsnelheid", "Spot wind voorsp.", windUnits),
      getColFromSpotHarm(
        data,
        "vlaagsnelheid",
        "Spot vlaag voorsp.",
        windUnits,
      ),
    ];
    const datasetsC: GraphData[] = [
      getColFromSpotHarm(data, "temp_2m", "spot temp. voorsp."),
    ];
    const datasetsHpa: GraphData[] = [
      getColFromSpotHarm(data, "press", "Spot luchtdruk voorsp."),
    ];
    return {
      deg: { data: { datasets: datasetsDeg } },
      "m/s": { data: { datasets: datasetsMS } },
      C: { data: { datasets: datasetsC } },
      hPa: { data: { datasets: datasetsHpa } },
    };
  }

  const spotHarmonieFiltered = rawSpotHarmonie
    ? filterSpotHarmonieByDay(rawSpotHarmonie, day, duringDayOnly)
    : [];

  function addNorthWindExtraPoints(dataset: any): any {
    const original = Array.isArray(dataset.data) ? dataset.data : [];
    const augmented: any[] = [];
    for (let i = 0; i < original.length; i++) {
      const curr = original[i];
      if (i > 0) {
        const prev = original[i - 1];
        const prevY = prev.y;
        const currY = curr.y;
        if (prevY != null && currY != null && Math.abs(prevY - currY) > 180) {
          const midX = (prev.x + curr.x) / 2;
          const firstY = prevY > currY ? 360 : 0;
          augmented.push({ x: midX - 1000, y: firstY });
          augmented.push({ x: midX, y: null });
          const lastY = prevY < currY ? 360 : 0;
          augmented.push({ x: midX + 1000, y: lastY });
        }
      }
      augmented.push(curr);
    }
    // return a new dataset with merged and sorted data
    return {
      ...dataset,
      data: augmented.sort((a, b) => a.x - b.x),
    };
  }

  const spotHarmonieFilteredTransformed =
    transformSpotHarmonie(spotHarmonieFiltered);

  return (
    <div>
      <SportSetting sport={sport} setSport={setSport} />
      {spotDropdownData ? (
        <>
          <select onChange={onSpotChange} value={spot}>
            {spotDropdownData.map((spot) => (
              <option key={spot.id} value={spot.id}>
                {spot.name}
              </option>
            ))}
          </select>{" "}
          <br />
        </>
      ) : (
        "loading"
      )}
      <button onClick={() => setDay("yesterday")}>Gisteren</button>
      <button onClick={() => setDay("today")}>Vandaag</button>
      <button onClick={() => setDay("tomorrow")}>Morgen</button>
      <button onClick={() => setDay("dayaftertomorrow")}>
        Overmorgen
      </button>{" "}
      <DuringDayOnlySwitch
        duringDayOnly={duringDayOnly}
        setDuringDayOnly={setDuringDayOnly}
      />
      <span>
        <label
          style={{
            marginLeft: 12,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <input
            type="checkbox"
            checked={showSpotHarmonie}
            onChange={(e) => setShowSpotHarmonie(e.target.checked)}
          />
          Toon spot harmonie
        </label>
        <InfoOverlay>
          <div style={{ fontSize: "1.2rem", marginTop: 16, textAlign: "left" }}>
            <p>
              De grafieken op de spot pagina geven voor de windmeter die het
              meest geschikt is voor de spot de meting en de voorspelling. Die
              voorspelling is dus ook voor de locatie van de windmeter. Omdat
              niet alle spots een goede windmeter in de buurt hebben, is de
              informatie die je in die grafiek ziet niet altijd even
              representatief voor de spot.
            </p>
            <p>
              Om beter te weten hoe representatief de meting en de voorspelling
              zijn voor de spot, kun je spot Harmonie aan zetten. Je krijgt dan
              in zwarte lijnen de voorspelling voor de daadwerkelijke gps
              coordinaten van de spot (ipv die van de windmeter) in de zelfde
              grafiek. Je kunt dan de voorspelling voor de spot vergelijken met
              die voor de locatie van de windmeter. Als ze sterk op elkaar
              lijken geeft dat meer vertrouwen in de windmeter. Ook kun je er,
              als de voorspelling van de spot meer wind aan geeft dan de
              voorspelling voor de windmeter, vanuit gaan dat de kans groot is
              dat het op de spot harder waait dan de windmeter aangeeft.
            </p>
          </div>
        </InfoOverlay>
      </span>
      <h1>
        {selectedActName} {day}
      </h1>
      <ul>
        <ConditionalRender
          condition={windLocationData && windLocationData.id !== 0}
        >
          <li>
            <ActueleWindMeting spotId={spot} location={windLocationData} />
          </li>
        </ConditionalRender>
        <ConditionalRender condition={sport === "kite"}>
          <ActueelGetij
            tideWindData={tideWindData}
            windUnits={windUnits}
            spotName={selectedSpotName}
            spotId={spot}
          />
        </ConditionalRender>
      </ul>
      <ConditionalRender condition={sport === "kite"}>
        <NkvSpotLinks spotId={spot} />{" "}
      </ConditionalRender>
      <br />
      {!selectedSpotData ? (
        <>
          <br />
          <br />
          <br />
          <br />
          <Center>Kies eerst een spot</Center>
        </>
      ) : (
        <div>
          {(selectedSpotRemarks ?? selectedActRemarks) == null ? "" : <br />}
          {selectedActRemarks && (
            <SanitizedDynamicHtml dynamicUnsafeHtml={selectedActRemarks} />
          )}
          {selectedSpotRemarks && (
            <SanitizedDynamicHtml dynamicUnsafeHtml={selectedSpotRemarks} />
          )}

          {selectedGraphs ? (
            <>
              {selectedGraphs.map((graph) => {
                return (
                  <SpotForecastGraph
                    location={locationById(graph.location_id)}
                    day={day}
                    unifiedUnit={graph.unifiedunits}
                    windUnits={windUnits}
                    activityId={activity}
                    key={graph.unifiedunits}
                    duringDayOnly={duringDayOnly}
                    windowSize={windowSize}
                    runsToShow="lastOnly"
                    showSpotHarmonie={showSpotHarmonie}
                    spotHarmData={spotHarmonieFilteredTransformed}
                  />
                );
              })}
            </>
          ) : (
            "loading"
          )}

          <TideGraphs
            spot={spot}
            tideWindData={filteredTideWindData}
            day={day}
            windowSize={windowSize}
            selectedSpotData={selectedSpotData}
            duringDayOnly={duringDayOnly}
            windUnits={windUnits}
          />

          {selectedExtraGraphs ? (
            <>
              {selectedExtraGraphs.map((graph) => {
                return (
                  <SpotForecastGraph
                    location={locationById(graph.location_id)}
                    day={day}
                    unifiedUnit={graph.unit}
                    key={graph.unit}
                    duringDayOnly={duringDayOnly}
                    windowSize={windowSize}
                    runsToShow="lastOnly"
                    showSpotHarmonie={showSpotHarmonie}
                    spotHarmData={spotHarmonieFilteredTransformed}
                  />
                );
              })}
            </>
          ) : (
            "loading"
          )}
          {activity ? (
            <ActivityChanceGraph
              key="chancegraph"
              activityId={activity}
              activityName={selectedActName}
              windowSize={windowSize}
              //TODO add run
            />
          ) : (
            ""
          )}
          <br />
          {activity ? (
            <ActivityLimitsTable
              activityId={activity}
              windUnits={windUnits}
              actLimitData={actLimitData}
            />
          ) : (
            ""
          )}
          <h4>Bron:</h4>
          <p>
            {selectedSpotData && locationData ? (
              <>
                <b>Windmetingen</b>:{" "}
                <MeasurementSourceLink
                  location={selectedSpotData.wind_location}
                  allLocationData={locationData}
                  linkLocationLocal={true}
                />
                <br />
                {selectedSpotData.temp_location ? (
                  <>
                    <b>Temperatuurmetingen</b>:{" "}
                    <MeasurementSourceLink
                      location={selectedSpotData.temp_location}
                      allLocationData={locationData}
                      linkLocationLocal={true}
                    />
                    <br />
                  </>
                ) : (
                  <></>
                )}
                {selectedSpotData.presure_location ? (
                  <>
                    <b>Luchtdrukmetingen</b>:{" "}
                    <MeasurementSourceLink
                      location={selectedSpotData.presure_location}
                      allLocationData={locationData}
                      linkLocationLocal={true}
                    />
                    <br />
                  </>
                ) : (
                  <></>
                )}
              </>
            ) : (
              <></>
            )}
            Voor de GPS locaties van alle weerstations zie:{" "}
            <a href={SC_BASE_PATH + "/weerstations.kml.php"}>
              google earth overzicht van de gebruikte weerstations
            </a>{" "}
            of de &quot;(maps)&quot; link van een specifiek weerstation.
            <br />
            <b>Weer voorspellingen in de meting grafieken op deze pagina</b>:
            Het Harmonie model van{" "}
            <a href="http://www.knmi.nl" target="blank">
              KNMI
            </a>
            . Voorspellingen worden alleen verzameld voor locaties waar ook een
            meting van beschikbaar is. De voorspelling is daarom de voorspelling
            voor de locatie van het bijbehorende meetstation en dus niet de
            locatie van de spot.
            <br />
            <ConditionalRender condition={sport === "kite"}>
              <b>Getij-voorspellingen</b>: Het{" "}
              <a
                href="https://rwsos.rws.nl/viewer/map/noordzee/stroming"
                target="_blank"
                rel="noreferrer"
              >
                Kuststrook-fijn model
              </a>{" "}
              van{" "}
              <a
                href="http://www.rijkswaterstaat.nl/"
                target="_blank"
                rel="noreferrer"
              >
                Rijkswaterstaat
              </a>
              . Dit model geeft de getijdehoogte en stroming met een combinatie
              van astronomisch getij en wind-invloeden volgens het Harmonie
              model van KNMI. De stroming tegen de wind in is een combinatie van
              de stromingsinformatie uit het kuststrook-fijn model met de
              windrichting uit het harmonie model. De stroomvector
              (kuststrook-fijn) wordt ontbonden in een component tegen de wind
              (harmonie) in en een component haaks op de wind. De component
              haaks op de wind wordt vervolgens weg gelaten omdat deze niet of
              nauwelijks van invloed is op de kwaliteit van een kitesessie.
              <br />
            </ConditionalRender>
          </p>
        </div>
      )}
    </div>
  );
};
